import { useState } from "react";
import { Link } from "react-router-dom";

import "../styles/homeRedesign.scss";

const Divider = ({ bg, fill }) => (
    <div className={`home-divider bg-${bg}`} aria-hidden="true">
        <svg viewBox="0 0 100 20" preserveAspectRatio="none" className="home-divider__svg">
            <polygon points="0,0 100,0 100,7 0,20" fill={fill === "ink" ? "#0a0f14" : "#f7f7f5"} />
            <polyline points="0,0 100,0 100,7 0,20" fill="none" stroke="#e67700" strokeWidth="0.6" strokeOpacity="0.55" />
        </svg>
    </div>
);

const CopyEmailButton = ({ email }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(email).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    return (
        <button
            type="button"
            className="home-copy-btn"
            aria-label="Copy email address"
            title="Copy email address"
            onClick={handleCopy}
        >
            {copied ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
            )}
        </button>
    );
};

const Home = () => (
    <div className="home-page">
        {/* Nav */}
        <header className="home-header bg-paper">
            <div className="home-header__inner">
                <Link to="/" className="home-header__brand">
                    <svg width="52" height="32" viewBox="0 0 160 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <rect x="4" y="4" width="152" height="92" rx="14" fill="#0a0f14" />
                        <circle cx="58" cy="50" r="17" stroke="#f7f7f5" strokeWidth="7" />
                        <circle cx="108" cy="50" r="12" fill="#e67700" />
                        <circle cx="108" cy="50" r="2.2" fill="#f7f7f5" />
                        <circle cx="58" cy="50" r="3.4" fill="#e67700" />
                    </svg>
                    Podsy
                </Link>
                <nav className="home-header__nav">
                    <Link to="/login" className="home-btn home-btn--outline">Login</Link>
                    <Link to="/signup" className="home-btn home-btn--dark">Sign up</Link>
                </nav>
                <button
                    className="home-header__toggle"
                    type="button"
                    aria-label="Menu"
                    data-bs-toggle="collapse"
                    data-bs-target="#home-mobile-nav"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" /></svg>
                </button>
            </div>
            <div className="collapse home-header__mobile" id="home-mobile-nav">
                <Link to="/login" className="home-btn home-btn--outline">Login</Link>
                <Link to="/signup" className="home-btn home-btn--dark">Sign up</Link>
            </div>
        </header>

        {/* Hero */}
        <section
            className="home-hero bg-ink"
            style={{ backgroundImage: "url(/img/hero-network.png)" }}
        >
            <div className="home-hero__scrim" aria-hidden="true" />
            <div className="home-hero__inner">
                <div className="home-hero__content">
                    <p className="home-eyebrow">Podsy: Podcast Club</p>
                    <h1 className="home-hero__heading">
                        <span className="text-nowrap-sm">People love your podcast.</span><br />
                        <span className="home-accent-text text-nowrap-sm">You just can't tell who.</span>
                    </h1>
                    <p className="home-hero__lede">
                        A Podcast Club turns your anonymous listeners into real
                        relationships, some become your clients, some become the community
                        you finally own.
                    </p>
                    <Link to="/demo" className="home-btn home-btn--cta">
                        See a Podcast Club
                    </Link>
                </div>
            </div>
        </section>

        <Divider bg="paper" fill="ink" />

        {/* Self-qualifier */}
        <section className="home-section home-glow-light-a bg-paper">
            <div className="home-narrow text-center">
                <h2 className="home-h2">Which of these sounds like your show?</h2>

                <div className="home-qualifier-list">
                    <div className="home-card home-card--light home-qualifier-card">
                        <span className="home-qualifier-card__num">01</span>
                        <p className="home-qualifier-card__text">
                            Some episodes land. Others don't, and you're not sure what worked.
                        </p>
                    </div>
                    <div className="home-card home-card--light home-qualifier-card">
                        <span className="home-qualifier-card__num">02</span>
                        <p className="home-qualifier-card__text">
                            People tell you the show is great. None of that has ever turned
                            into a client.
                        </p>
                    </div>
                    <div className="home-card home-card--light home-qualifier-card">
                        <span className="home-qualifier-card__num">03</span>
                        <p className="home-qualifier-card__text">
                            Listeners tell you it meant something. You never find out what
                            they did with it.
                        </p>
                    </div>
                </div>

                <p className="home-qualifier-outro">
                    If any of these sound familiar, here's how a Podcast Club solves those
                    problems.
                </p>

                <div className="home-cta-stack">
                    <Link to="/demo" className="home-btn home-btn--cta home-btn--sm">
                        See a Podcast Club
                    </Link>
                    <a
                        href="https://calendly.com/skilldingkevin/podsy-1st-meeting"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="home-link-muted"
                    >
                        Or book a call
                    </a>
                </div>
            </div>
        </section>

        <Divider bg="ink" fill="paper" />

        {/* The problem */}
        <section
            className="home-problem bg-ink"
            style={{ backgroundImage: "url(/img/problem-waveform.png)" }}
        >
            <div className="home-problem__scrim" aria-hidden="true" />
            <div className="home-problem__inner">
                <div className="home-hero__content">
                    <p className="home-eyebrow">The problem</p>
                    <h2 className="home-h2 home-h2--left text-white">
                        The problem isn't your podcast. It's what happens after it ends.
                    </h2>
                    <p className="home-problem__lede">
                        Every episode you release disappears into a feed. Downloads tell you
                        how many people pressed play. Nothing tells you:
                    </p>
                    <ul className="home-bullet-list">
                        <li><span className="home-bullet-list__dot">•</span><span>Whether something shifted for them</span></li>
                        <li><span className="home-bullet-list__dot">•</span><span>Whether they acted on what they heard</span></li>
                        <li><span className="home-bullet-list__dot">•</span><span>Whether the conversation you started in their earphones continued anywhere at all</span></li>
                    </ul>
                    <p className="home-problem__closer">
                        That's the gap Podsy is built around.
                    </p>
                </div>
            </div>
        </section>

        <Divider bg="paper" fill="ink" />

        {/* What a Podcast Club does */}
        <section className="home-section home-glow-light-b bg-paper">
            <div className="home-medium text-center">
                <p className="home-eyebrow home-eyebrow--center">What Podsy delivers</p>
                <h2 className="home-h2">3 things a Podcast Club does for your show</h2>
            </div>

            <div className="home-wide">
                <div className="home-deliver-grid">
                    <div className="home-card home-card--light home-deliver-card">
                        <div className="home-deliver-card__num">1</div>
                        <h3 className="home-h3">You see what actually worked</h3>
                        <ul className="home-bullet-list home-bullet-list--card">
                            <li><span className="home-bullet-list__dot">•</span><span>Each week, a discussion prompt built from that episode opens a group conversation</span></li>
                            <li><span className="home-bullet-list__dot">•</span><span>Listeners also get a private set of actionable insights pulled from that same episode, something to try on their own before the next one</span></li>
                            <li><span className="home-bullet-list__dot">•</span><span>Every previous week stays open, so nobody falls behind and the conversation keeps building</span></li>
                        </ul>
                        <div className="home-deliver-card__means">
                            <p className="home-deliver-card__means-label">...And that means</p>
                            <p className="home-deliver-card__means-text">You see, in their own words, which ideas actually moved people.</p>
                        </div>
                    </div>
                    <div className="home-card home-card--light home-deliver-card">
                        <div className="home-deliver-card__num">2</div>
                        <h3 className="home-h3">Your listeners become visible</h3>
                        <ul className="home-bullet-list home-bullet-list--card">
                            <li><span className="home-bullet-list__dot">•</span><span>Passive listeners become participants, responding to a prompt instead of just pressing play</span></li>
                            <li><span className="home-bullet-list__dot">•</span><span>People who'd never raise their hand publicly get a private way to engage, one on one with you</span></li>
                            <li><span className="home-bullet-list__dot">•</span><span>Every response is tied to a real listener, not an anonymous download</span></li>
                        </ul>
                        <div className="home-deliver-card__means">
                            <p className="home-deliver-card__means-label">...And that means</p>
                            <p className="home-deliver-card__means-text">Your listeners stop being a download number and become real people having a conversation with you.</p>
                        </div>
                    </div>
                    <div className="home-card home-card--light home-deliver-card home-deliver-card--last">
                        <div className="home-deliver-card__num">3</div>
                        <h3 className="home-h3">Conversations start that wouldn't have</h3>
                        <ul className="home-bullet-list home-bullet-list--card">
                            <li><span className="home-bullet-list__dot">•</span><span>From day one, a listener can move from the group thread into a private conversation with you</span></li>
                            <li><span className="home-bullet-list__dot">•</span><span>For some shows, that becomes a warm introduction to a potential client</span></li>
                            <li><span className="home-bullet-list__dot">•</span><span>For others, it becomes a subscription or an offer, built directly from listener depth</span></li>
                        </ul>
                        <div className="home-deliver-card__means">
                            <p className="home-deliver-card__means-label">...And that means</p>
                            <p className="home-deliver-card__means-text">Either way, the relationship already forming has somewhere to go.</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="home-medium text-center">
                <p className="home-deliver-footnote">
                    Every well-run Club produces the first two. The third shows up when
                    the timing's right.
                </p>
                <a
                    href="https://calendly.com/skilldingkevin/podsy-1st-meeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="home-link-muted"
                >
                    Book a call
                </a>
            </div>
        </section>

        <Divider bg="ink" fill="paper" />

        {/* How it works */}
        <section className="home-section home-glow-dark-c bg-ink">
            <div className="home-medium">
                <h2 className="home-h2 text-white">How it works</h2>
                <p className="home-how-lede">
                    A Podcast Club is an invite-only, six-week standalone season built
                    around your show → up to six episodes from your back catalogue.
                </p>

                <div className="home-how-grid">
                    <div className="home-card home-card--raised">
                        <div className="home-how-card__num">1</div>
                        <h3 className="home-h3 text-white">Add your RSS feed</h3>
                        <p className="home-how-card__text">
                            Podsy drafts your Club automatically from your existing episodes.
                            No rebuild, no new episodes needed. Review each week and ship
                            as-is or refine it.
                        </p>
                    </div>
                    <div className="home-card home-card--raised">
                        <div className="home-how-card__num">2</div>
                        <h3 className="home-h3 text-white">Listeners engage</h3>
                        <p className="home-how-card__text">
                            Each week, listeners get a discussion prompt tied to the episode,
                            and a set of private, actionable insights just for them:
                            something to try on their own, visible only if they choose to
                            share it. A structured way to think and act between episodes.
                        </p>
                    </div>
                    <div className="home-card home-card--raised">
                        <div className="home-how-card__num">3</div>
                        <h3 className="home-h3 text-white">You see what worked</h3>
                        <p className="home-how-card__text">
                            A clear dashboard showing who engaged, which topics resonated, and
                            what patterns are emerging across your audience. No guesswork.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <Divider bg="paper" fill="ink" />

        {/* Founder */}
        <section className="home-section home-section--sm bg-paper">
            <div className="home-narrow-sm">
                <img
                    src="/img/kevin.jpg"
                    alt="Kevin, founder of Podsy"
                    className="home-founder-avatar"
                />
                <p className="home-founder-text">
                    <span className="home-founder-text__name">I'm Kevin, founder of Podsy.</span>{" "}
                    By the time our show passed 30,000 downloads, I realised the number
                    meant nothing: no leads, no revenue, barely a reply. That's the moment
                    I knew something had to change, and Podsy is what I built instead.
                </p>
            </div>
        </section>

        <Divider bg="ink" fill="paper" />

        {/* Closing CTA */}
        <section className="home-section home-section--sm home-glow-dark-a bg-ink text-center">
            <div className="home-narrow-sm">
                <h2 className="home-h2 text-white">See a Podcast Club</h2>
                <p className="home-closing-lede">
                    See one built, or build your own in about fifteen minutes.
                </p>
                <div className="home-cta-stack">
                    <Link to="/demo" className="home-btn home-btn--cta">
                        See a Podcast Club
                    </Link>
                    <a
                        href="https://calendly.com/skilldingkevin/podsy-1st-meeting"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="home-link-muted home-link-muted--dark"
                    >
                        Or get in touch
                    </a>
                </div>
            </div>
        </section>

        {/* Footer */}
        <footer className="home-footer bg-paper">
            <p>Podsy turns your listeners into people you can actually talk to.</p>
            <p className="home-footer__contact">
                Partnerships and other enquiries:{" "}
                <span className="home-footer__email">
                    <a href="mailto:partnerships@podsy.io">partnerships@podsy.io</a>
                    <CopyEmailButton email="partnerships@podsy.io" />
                </span>
            </p>
            <p className="home-footer__legal">
                <a href="#top">Privacy</a>
                <span className="home-footer__sep">·</span>
                <a href="#top">Terms</a>
                <span className="home-footer__sep">·</span>
                © Podsy 2026
            </p>
        </footer>
    </div>
);

export default Home;
